import React, { useState } from 'react';
import { FlexCrate, Link, Text } from '@perch-labs/shrike';
import { copyFor, routes } from 'config';
import { trackClickEvent } from 'domain/tracking';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getPaymentCopy, PaymentPlan } from '../../../payment-methods.utils';
import { PaymentCard } from '../../../payment-methods.style';
import { getPlanPaymentError } from './plan-methods-row.utils';
import {
  StyledHeader,
  StyledButton,
  StyledIconButton,
  StyledRowCrate,
  StyledIcon,
  StyledErrorCard,
} from './plan-methods-row.style';

const getCopy = copyFor('settings.paymentInformation');
interface Props {
  index: number;
  plan: PaymentPlan;
  counts: { methodCount: number; activePlanCount: number };
  openAddPaymentMethodModal: () => void;
}

export const PlanMethodsRow = ({
  index,
  plan,
  counts,
  openAddPaymentMethodModal,
}: Props) => {
  const flags = useFlags();
  const {
    id: planId,
    paymentMethod,
    displayName,
    mostRecentStatement,
    utilityAccounts = [],
  } = plan;
  const { methodCount, activePlanCount } = counts;
  const initialToggle = activePlanCount === 1;

  // Feature flag: cs-acb-retirement
  // When enabled, use targetBillExperience to determine if payment method is needed
  const csAcbRetirement = flags.csAcbRetirement;
  const UTILITY_BILLED = 'utility_billed';
  const utilityBilled = utilityAccounts.every(
    ({ targetBillExperience, activeBillingSettings, funnelBillExperience }) => {
      const billExperience = csAcbRetirement
        ? (targetBillExperience as string) ??
          activeBillingSettings?.billExperience ??
          funnelBillExperience
        : activeBillingSettings?.billExperience ?? funnelBillExperience;
      return billExperience === UTILITY_BILLED;
    }
  );
  const [accordionIsOpen, setAccordionIsOpen] = useState(initialToggle);
  const ButtonLink =
    methodCount > 1 ? (
      <Link
        className="buttonLink"
        type="CTA"
        backgroundless
        to={routes({ planId }).paymentMethodUpdate}
        onClick={() =>
          trackClickEvent('paymentInformation.planMethods.changePaymentMethod')
        }
        fullWidth
        margin={{ top: '16px' }}
      >
        {getCopy('paymentMethods.changePaymentMethod')}
      </Link>
    ) : (
      <StyledButton
        className="buttonLink"
        onClick={() => {
          openAddPaymentMethodModal();
          trackClickEvent('paymentInformation.planMethods.addPaymentMethod');
        }}
      >
        {getCopy('paymentMethods.addPaymentMethod')}
      </StyledButton>
    );

  const paymentDetails = () => {
    if (!paymentMethod) return null;
    const { label, text } = getPaymentCopy({
      paymentMethod,
      labelProps: { tag: 'h4' },
    });
    return (
      <>
        <Text color="secondary" margin={{ top: '24px', bottom: '8px' }}>
          {getCopy('planMethods.planSubtitle')}
        </Text>
        <PaymentCard>
          {label}
          {text}
        </PaymentCard>
      </>
    );
  };

  const planPaymentError = getPlanPaymentError({
    paymentMethod,
    mostRecentStatement,
    utilityBilled,
  });

  return (
    <StyledRowCrate
      paddingLeftMobile="24px"
      paddingRightMobile="24px"
      hasTopBorder={index === 0}
      onClick={e => {
        if (e.target.nodeName !== 'BUTTON') {
          setAccordionIsOpen(!accordionIsOpen);
        }
      }}
    >
      <StyledHeader accordionIsOpen={accordionIsOpen}>
        <FlexCrate
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          padding="0px"
        >
          <FlexCrate alignItems="center">
            <Text tag="h3" textStyle="heading700">
              {displayName}
            </Text>
            {planPaymentError && (
              <StyledIcon
                margin={{ left: '16px' }}
                aria-label={getCopy('paymentMethods.errors.ariaLabel')}
                data-testid="error-icon"
              />
            )}
          </FlexCrate>
          <StyledIconButton
            data-testid="toggle-plan-methods-accordion"
            aria-label={getCopy('planMethods.ariaButtonLabel', {
              action: accordionIsOpen ? 'Hide' : 'Reveal',
            })}
            onClick={() => setAccordionIsOpen(!accordionIsOpen)}
            icon="ChevronDown"
          />
        </FlexCrate>
      </StyledHeader>
      {accordionIsOpen && (
        <FlexCrate flexDirection="column">
          {paymentDetails()}
          {planPaymentError && (
            <StyledErrorCard>
              <FlexCrate>
                <StyledIcon />
                <Text margin={{ left: '16px' }}>{planPaymentError}</Text>
              </FlexCrate>
            </StyledErrorCard>
          )}
          {!utilityBilled && ButtonLink}
        </FlexCrate>
      )}
    </StyledRowCrate>
  );
};
